import { useMutation, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { Player, TeamGameMatchPlayMatch } from '../../database.schema';
import { AWAITING_SCORES, MATCH_FORMATS } from '../app/constants';
import { getTourGroup } from '../utils/get-tour-group';

type TeamsData = {
  selectedTournamentIndex: number;
  team1Name: string;
  team1: Array<Player>;
  team2Name: string;
  team2: Array<Player>;
};

const mutateTeams = async (teamsData: TeamsData): Promise<TeamsData> => {
  const { selectedTournamentIndex, team1Name, team1, team2Name, team2 } = teamsData;
  const tourGroup = getTourGroup();
  const authToken = Cookies.get('token');

  const year = new Date().getFullYear();
  var dbDNS = `https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app/tournaments/${tourGroup}/${selectedTournamentIndex}/ryderCup/.json?auth=${authToken}`;
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = `http://127.0.0.1:9000/tournaments/${tourGroup}/${selectedTournamentIndex}/ryderCup/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  }
  const response = await fetch(dbDNS, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      team1: { name: team1Name, points: 0, playerIds: [...team1.map((player) => player.id)] },
      team2: { name: team2Name, points: 0, playerIds: [...team2.map((player) => player.id)] },
      matches: [],
    }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await response.json();
};

export const useTournamentTeamsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutateTeams,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['all-data'] });
    },
  });
};

type TeamGamesTeamsData = {
  selectedTournamentIndex: number;
  nextMatchIndex: number;
  team1: Array<Player>;
  team2: Array<Player>;
  status: string;
  courseId: number;
  startingHole: number;
  matchFormat: string;
  singlesMatches?: Array<TeamGameMatchPlayMatch>;
};

const mutateTeamGamesTeams = async (teamsData: TeamGamesTeamsData): Promise<TeamGamesTeamsData> => {
  const { selectedTournamentIndex, team1, team2, nextMatchIndex, courseId, startingHole, matchFormat } = teamsData;
  const tourGroup = getTourGroup();
  const authToken = Cookies.get('token');

  const year = new Date().getFullYear();
  var dbDNS = `https://golf-tour-462cf-default-rtdb.europe-west1.firebasedatabase.app/tournaments/${tourGroup}/${selectedTournamentIndex}/teamGames/matches/${nextMatchIndex}/.json?auth=${authToken}`;
  if (process.env.REACT_APP_MODE === 'dev') {
    dbDNS = `http://127.0.0.1:9000/tournaments/${tourGroup}/${selectedTournamentIndex}/teamGames/matches/${nextMatchIndex}/.json?ns=golf-tour-462cf-default-rtdb&auth=${authToken}`;
  }
  const teams = [];
  const matchPlayMatches: Array<TeamGameMatchPlayMatch> = [];
  teams.push({
    scoresEntered: false,
    points: 0,
    players: team1.map((player: Player) => ({
      playerId: player.id,
      points: 0,
      round: { courseHandicap: -1, individualHandicap: -1, playerId: player.id, points: 0, scoreCard: [] },
    })),
  });
  teams.push({
    scoresEntered: false,
    points: 0,
    players: team2.map((player: Player) => ({
      playerId: player.id,
      points: 0,
      round: { courseHandicap: -1, individualHandicap: -1, playerId: player.id, points: 0, scoreCard: [] },
    })),
  });

  if (matchFormat === MATCH_FORMATS.singles.id) {
    team1.forEach((_: Player, index: number) => {
      matchPlayMatches.push({
        team1: {
          playerIds: [team1[index].id],
          points: 0,
        },
        team2: {
          playerIds: [team2[index].id],
          points: 0,
        },
        resultByHole: [],
      });
    });
  }
  if (matchFormat === MATCH_FORMATS.pairs.id) {
    matchPlayMatches.push({
      team1: {
        playerIds: [team1[0].id, team1[1].id],
        points: 0,
      },
      team2: {
        playerIds: [team2[0].id, team2[1].id],
        points: 0,
      },
      resultByHole: [],
    });
    matchPlayMatches.push({
      team1: {
        playerIds: [team1[2].id, team1[3].id],
        points: 0,
      },
      team2: {
        playerIds: [team2[2].id, team2[3].id],
        points: 0,
      },
      resultByHole: [],
    });
  }
  const response = await fetch(dbDNS, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      status: AWAITING_SCORES,
      teams,
      courseId,
      startingHole,
      matchFormat,
      matchPlayMatches,
    }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await response.json();
};

export const useTournamentTeamGamesTeamsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutateTeamGamesTeams,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['all-data'] });
    },
  });
};
